import { useEffect, useMemo, useState } from 'react';
import { Modal, Form, Button, Table, Alert, Badge, Spinner, Row, Col, InputGroup } from 'react-bootstrap';
import { Plus, Trash, BoxArrowUpRight, Clipboard, ArrowClockwise } from 'react-bootstrap-icons';
import { useAuth } from 'react-oidc-context';
import { Case } from '../../types';
import { Invoice, InvoiceDraftRequest, InvoiceLineItem, OrgStripeStatus } from '../../types/admin';
import { createInvoice, updateInvoice, deleteInvoice, invoiceAction } from '../../api/admin';
import { PAYMENT_METHOD_OPTIONS } from '../../constants/constants';
import { formatDateTime } from '../../utils/dateFormatters';
import { money, formatDay, STATUS_VARIANTS } from '../../utils/invoiceFormat';

const isoDay = (offsetDays = 0) => {
  const d = new Date();
  d.setDate(d.getDate() + offsetDays);
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
};

const lineTotal = (li: { quantity: number | string; unit_amount: number | string }) =>
  Math.round(Number(li.quantity || 0) * Number(li.unit_amount || 0) * 100) / 100;

const emptyLine = () => ({ description: '', quantity: '1', unit_amount: '' });

type LineForm = { description: string; quantity: string; unit_amount: string };

const errorMessage = (err: any) => err.response?.data?.error || err.message;

interface InvoiceModalProps {
  show: boolean;
  invoice: Invoice | null; // null = new invoice
  cases: Case[];
  stripe: Record<string, OrgStripeStatus>;
  initialCaseId?: string;
  onClose: () => void;
  onSaved: (invoice: Invoice | null, message: string) => void;
}

export default function InvoiceModal({ show, invoice, cases, stripe, initialCaseId, onClose, onSaved }: InvoiceModalProps) {
  const auth = useAuth();
  const token = auth.user?.id_token;
  const isDraft = !invoice || invoice.status === 'draft';

  const [caseId, setCaseId] = useState('');
  const [customerName, setCustomerName] = useState('');
  const [customerEmail, setCustomerEmail] = useState('');
  const [dueDate, setDueDate] = useState(isoDay(30));
  const [lines, setLines] = useState<LineForm[]>([emptyLine()]);
  const [memo, setMemo] = useState('');
  const [footer, setFooter] = useState('');
  const [internalNotes, setInternalNotes] = useState('');

  const [busy, setBusy] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [confirming, setConfirming] = useState<'delete' | 'void' | 'finalize' | 'finalize_send' | null>(null);
  const [showMarkPaid, setShowMarkPaid] = useState(false);
  const [paidMethod, setPaidMethod] = useState('Check');
  const [paidReference, setPaidReference] = useState('');
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    if (!show) return;
    setError(null);
    setConfirming(null);
    setShowMarkPaid(false);
    setCopied(false);
  }, [show]);

  // Keyed on the id (not the object) so a refreshed copy of the same invoice
  // doesn't wipe in-progress edits; a failed finalize swaps a new invoice for
  // its saved draft, which carries the same values.
  useEffect(() => {
    if (!show) return;
    if (invoice) {
      setCaseId(invoice.case_id);
      setCustomerName(invoice.customer_name);
      setCustomerEmail(invoice.customer_email);
      setDueDate(invoice.due_date || isoDay(30));
      setLines(invoice.line_items.map(li => ({
        description: li.description, quantity: String(li.quantity), unit_amount: String(li.unit_amount),
      })));
      setMemo(invoice.memo || '');
      setFooter(invoice.footer || '');
      setInternalNotes(invoice.internal_notes || '');
    } else {
      const c = cases.find(x => x.case_id === initialCaseId);
      setCaseId(c?.case_id || '');
      setCustomerName(c?.client || '');
      setCustomerEmail('');
      setDueDate(isoDay(30));
      setLines([emptyLine()]);
      setMemo('');
      setFooter('');
      setInternalNotes('');
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [show, invoice?.invoice_id, invoice?.status]);

  const selectedCase = cases.find(c => c.case_id === caseId);
  const orgId = invoice?.organization_id || selectedCase?.organization_id || '';
  const orgStripe = stripe[orgId];
  const total = useMemo(() => lines.reduce((sum, li) => sum + lineTotal(li), 0), [lines]);

  const chooseCase = (id: string) => {
    setCaseId(id);
    const c = cases.find(x => x.case_id === id);
    if (c && !customerName) setCustomerName(c.client || '');
  };

  const updateLine = (i: number, field: keyof LineForm, value: string) =>
    setLines(ls => ls.map((li, idx) => (idx === i ? { ...li, [field]: value } : li)));

  const draftBody = (): InvoiceDraftRequest => ({
    case_id: caseId,
    customer_name: customerName,
    customer_email: customerEmail,
    due_date: dueDate,
    line_items: lines
      .filter(li => li.description.trim() || li.unit_amount)
      .map<InvoiceLineItem>(li => ({
        description: li.description, quantity: Number(li.quantity), unit_amount: Number(li.unit_amount),
      })),
    memo,
    footer,
    internal_notes: internalNotes,
  });

  const run = async (label: string, fn: () => Promise<void>) => {
    if (busy) return;
    setBusy(label);
    setError(null);
    try {
      await fn();
    } catch (err: any) {
      setError(errorMessage(err));
    } finally {
      setBusy(null);
      setConfirming(null);
    }
  };

  const saveDraft = () => run('save', async () => {
    const saved = invoice
      ? await updateInvoice(invoice.invoice_id, draftBody(), token)
      : await createInvoice(draftBody(), token);
    onSaved(saved, 'Draft saved');
  });

  const finalize = (sendEmail: boolean) => run('finalize', async () => {
    // Persist the latest edits first so Stripe gets exactly what's on screen.
    const saved = invoice
      ? await updateInvoice(invoice.invoice_id, draftBody(), token)
      : await createInvoice(draftBody(), token);
    try {
      const finalized = await invoiceAction(saved.invoice_id, 'finalize', token, { send_email: sendEmail });
      onSaved(finalized, sendEmail ? `Invoice ${finalized.stripe_number} finalized and emailed` : `Invoice ${finalized.stripe_number} finalized`);
    } catch (err) {
      onSaved(saved, ''); // keep the saved draft in the list, but surface the error here
      throw err;
    }
  });

  const remove = () => run('delete', async () => {
    await deleteInvoice(invoice!.invoice_id, token);
    onSaved(null, 'Draft deleted');
  });

  const action = (name: 'send' | 'void' | 'refresh' | 'mark_paid', message: string, body?: Record<string, string>) =>
    run(name, async () => {
      const updated = await invoiceAction(invoice!.invoice_id, name, token, body);
      onSaved(updated, message);
    });

  const saveNotes = () => run('notes', async () => {
    const updated = await updateInvoice(invoice!.invoice_id, { internal_notes: internalNotes }, token);
    onSaved(updated, 'Internal notes saved');
  });

  const copyLink = async () => {
    if (!invoice?.hosted_invoice_url) return;
    await navigator.clipboard.writeText(invoice.hosted_invoice_url);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  const stripeBlocked = !orgStripe?.configured;

  return (
    <Modal show={show} onHide={onClose} size="lg" backdrop="static">
      <Modal.Header closeButton>
        <Modal.Title>
          {!invoice ? 'New invoice' : invoice.stripe_number ? `Invoice ${invoice.stripe_number}` : 'Draft invoice'}
          {invoice && <Badge bg={STATUS_VARIANTS[invoice.status]} className="ms-2 align-middle">{invoice.status}</Badge>}
          {invoice?.status !== 'draft' && invoice?.livemode === false && (
            <Badge bg="warning" text="dark" className="ms-2 align-middle">test mode</Badge>
          )}
        </Modal.Title>
      </Modal.Header>
      <Modal.Body>
        {error && <Alert variant="danger" dismissible onClose={() => setError(null)}>{error}</Alert>}

        {isDraft ? (
          <Form onSubmit={e => e.preventDefault()}>
            <Row className="g-3 mb-3">
              <Col md={12}>
                <Form.Label>Case</Form.Label>
                {invoice ? (
                  <div><strong>{invoice.case_number}</strong> — {invoice.case_title}</div>
                ) : (
                  <Form.Select value={caseId} onChange={e => chooseCase(e.target.value)} required>
                    <option value="">Choose a case…</option>
                    {cases.map(c => (
                      <option key={c.case_id} value={c.case_id}>
                        {c.case_number} — {c.case_title}{c.client ? ` (${c.client})` : ''}
                      </option>
                    ))}
                  </Form.Select>
                )}
              </Col>
              <Col md={6}>
                <Form.Label>Bill to (name)</Form.Label>
                <Form.Control value={customerName} onChange={e => setCustomerName(e.target.value)} placeholder="Client or attorney name" />
              </Col>
              <Col md={6}>
                <Form.Label>Bill to (email)</Form.Label>
                <Form.Control type="email" value={customerEmail} onChange={e => setCustomerEmail(e.target.value)} placeholder="Where Stripe sends the invoice" />
              </Col>
              <Col md={4}>
                <Form.Label>Due date</Form.Label>
                <Form.Control type="date" value={dueDate} min={isoDay(0)} onChange={e => setDueDate(e.target.value)} />
              </Col>
            </Row>

            <Table size="sm" className="mb-1 align-middle">
              <thead>
                <tr className="small text-muted">
                  <th>Description</th>
                  <th style={{ width: 90 }}>Qty / hrs</th>
                  <th style={{ width: 130 }}>Unit price</th>
                  <th style={{ width: 110 }} className="text-end">Amount</th>
                  <th style={{ width: 40 }}></th>
                </tr>
              </thead>
              <tbody>
                {lines.map((li, i) => (
                  <tr key={i}>
                    <td>
                      <Form.Control size="sm" value={li.description} placeholder="e.g. Forensic review of bank statements"
                        onChange={e => updateLine(i, 'description', e.target.value)} />
                    </td>
                    <td>
                      <Form.Control size="sm" type="number" min="0" step="0.25" value={li.quantity}
                        onChange={e => updateLine(i, 'quantity', e.target.value)} />
                    </td>
                    <td>
                      <InputGroup size="sm">
                        <InputGroup.Text>$</InputGroup.Text>
                        <Form.Control type="number" min="0" step="0.01" value={li.unit_amount}
                          onChange={e => updateLine(i, 'unit_amount', e.target.value)} />
                      </InputGroup>
                    </td>
                    <td className="text-end">{money(lineTotal(li))}</td>
                    <td>
                      <Button variant="link" size="sm" className="text-danger p-0" disabled={lines.length === 1}
                        onClick={() => setLines(ls => ls.filter((_, idx) => idx !== i))} title="Remove line">
                        <Trash />
                      </Button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </Table>
            <div className="d-flex justify-content-between align-items-center mb-3">
              <Button variant="outline-secondary" size="sm" onClick={() => setLines(ls => [...ls, emptyLine()])}>
                <Plus /> Add line
              </Button>
              <div className="fw-semibold">Total {money(total)}</div>
            </div>

            <Form.Group className="mb-3">
              <Form.Label>Memo <span className="text-muted small">(shown on the invoice)</span></Form.Label>
              <Form.Control as="textarea" rows={3} value={memo} onChange={e => setMemo(e.target.value)}
                placeholder="e.g. Services rendered March 1–31 for the Smith v. Smith dissolution matter." />
            </Form.Group>
            <Form.Group className="mb-3">
              <Form.Label>Footer <span className="text-muted small">(payment terms, thank-you note — shown on the invoice)</span></Form.Label>
              <Form.Control as="textarea" rows={2} value={footer} onChange={e => setFooter(e.target.value)} />
            </Form.Group>
            <Form.Group>
              <Form.Label>Internal notes <span className="text-muted small">(never shown to the client)</span></Form.Label>
              <Form.Control as="textarea" rows={2} value={internalNotes} onChange={e => setInternalNotes(e.target.value)} />
            </Form.Group>

            {stripeBlocked && orgId && (
              <Alert variant="warning" className="mt-3 mb-0 small">
                Stripe isn't connected for this organization yet. You can save drafts now and finalize them once it is.
              </Alert>
            )}
            {orgStripe?.configured && !orgStripe.livemode && (
              <Alert variant="info" className="mt-3 mb-0 small">
                Stripe is in <strong>test mode</strong> for this organization — finalized invoices won't charge real money.
              </Alert>
            )}
          </Form>
        ) : (
          invoice && (
            <>
              <Row className="mb-3">
                <Col md={6}>
                  <div className="small text-muted">Case</div>
                  <div><strong>{invoice.case_number}</strong> — {invoice.case_title}</div>
                  <div className="small text-muted mt-2">Bill to</div>
                  <div>{invoice.customer_name}</div>
                  <div className="small">{invoice.customer_email}</div>
                </Col>
                <Col md={6} className="text-md-end">
                  <div className="small text-muted">Amount</div>
                  <div className="fs-4 fw-semibold">{money(invoice.amount_due ?? invoice.subtotal)}</div>
                  <div className="small">
                    Paid {money(invoice.amount_paid)} · Remaining {money(invoice.amount_remaining)}
                  </div>
                  <div className="small text-muted mt-1">Due {formatDay(invoice.due_date)}</div>
                  {invoice.paid_at && (
                    <div className="small text-success">
                      Paid {formatDateTime(invoice.paid_at)}{invoice.payment_method ? ` · ${invoice.payment_method}` : ''}
                    </div>
                  )}
                </Col>
              </Row>

              {invoice.hosted_invoice_url && invoice.status === 'open' && (
                <InputGroup size="sm" className="mb-3">
                  <InputGroup.Text>Payment link</InputGroup.Text>
                  <Form.Control readOnly value={invoice.hosted_invoice_url} onFocus={e => e.target.select()} />
                  <Button variant="outline-secondary" onClick={copyLink}><Clipboard /> {copied ? 'Copied' : 'Copy'}</Button>
                  <Button variant="outline-secondary" href={invoice.hosted_invoice_url} target="_blank" rel="noreferrer">
                    <BoxArrowUpRight />
                  </Button>
                </InputGroup>
              )}

              <Table size="sm" className="mb-3">
                <thead>
                  <tr className="small text-muted">
                    <th>Description</th><th className="text-end">Qty</th><th className="text-end">Unit</th><th className="text-end">Amount</th>
                  </tr>
                </thead>
                <tbody>
                  {invoice.line_items.map((li, i) => (
                    <tr key={i}>
                      <td>{li.description}</td>
                      <td className="text-end">{li.quantity}</td>
                      <td className="text-end">{money(li.unit_amount)}</td>
                      <td className="text-end">{money(lineTotal(li))}</td>
                    </tr>
                  ))}
                </tbody>
              </Table>

              {invoice.memo && (
                <div className="mb-2"><div className="small text-muted">Memo</div><div style={{ whiteSpace: 'pre-wrap' }}>{invoice.memo}</div></div>
              )}
              {invoice.footer && (
                <div className="mb-2"><div className="small text-muted">Footer</div><div style={{ whiteSpace: 'pre-wrap' }}>{invoice.footer}</div></div>
              )}
              {invoice.ledger_entry_id && (
                <div className="small text-muted mb-2">Recorded in the case's Time &amp; Payments ledger.</div>
              )}

              <Form.Group className="mb-3">
                <Form.Label className="small text-muted mb-1">Internal notes (never shown to the client)</Form.Label>
                <Form.Control as="textarea" rows={2} value={internalNotes} onChange={e => setInternalNotes(e.target.value)} />
                {internalNotes !== (invoice.internal_notes || '') && (
                  <Button size="sm" variant="outline-primary" className="mt-1" onClick={saveNotes} disabled={!!busy}>Save notes</Button>
                )}
              </Form.Group>

              {showMarkPaid && (
                <Alert variant="light" className="border">
                  <div className="small mb-2">
                    Record a payment received outside Stripe. The invoice is marked paid in Stripe and the payment is
                    added to the case's Time &amp; Payments ledger.
                  </div>
                  <Row className="g-2 align-items-end">
                    <Col md={4}>
                      <Form.Label className="small">Method</Form.Label>
                      <Form.Select size="sm" value={paidMethod} onChange={e => setPaidMethod(e.target.value)}>
                        {PAYMENT_METHOD_OPTIONS.map(m => <option key={m}>{m}</option>)}
                      </Form.Select>
                    </Col>
                    <Col md={5}>
                      <Form.Label className="small">Reference (check #, etc.)</Form.Label>
                      <Form.Control size="sm" value={paidReference} onChange={e => setPaidReference(e.target.value)} />
                    </Col>
                    <Col md={3}>
                      <Button size="sm" variant="success" className="w-100" disabled={!!busy}
                        onClick={() => action('mark_paid', 'Invoice marked paid', { method: paidMethod, reference: paidReference })}>
                        {busy === 'mark_paid' ? <Spinner size="sm" /> : 'Mark paid'}
                      </Button>
                    </Col>
                  </Row>
                </Alert>
              )}

              {invoice.history?.length > 0 && (
                <details className="small">
                  <summary className="text-muted">History</summary>
                  <ul className="mb-0 mt-1">
                    {[...invoice.history].reverse().map((h, i) => (
                      <li key={i}>
                        {formatDateTime(h.at)} — {h.event.replace('status:', 'status → ')}{h.detail ? ` (${h.detail})` : ''}
                        <span className="text-muted"> · {h.by}</span>
                      </li>
                    ))}
                  </ul>
                </details>
              )}
            </>
          )
        )}

        {confirming && (
          <Alert variant={confirming === 'delete' || confirming === 'void' ? 'danger' : 'primary'} className="mt-3 mb-0">
            {confirming === 'delete' && 'Delete this draft? This cannot be undone.'}
            {confirming === 'void' && 'Void this invoice? The payment link stops working and it can no longer be paid. The voided invoice stays on record.'}
            {confirming === 'finalize' && `Finalize this invoice for ${money(total)}? Stripe assigns an invoice number and payment link, and the invoice can no longer be edited (only voided).`}
            {confirming === 'finalize_send' && `Finalize this invoice for ${money(total)} and email it to ${customerEmail}? It can no longer be edited afterwards (only voided).`}
            <div className="mt-2 d-flex gap-2">
              <Button size="sm" variant={confirming === 'delete' || confirming === 'void' ? 'danger' : 'primary'} disabled={!!busy}
                onClick={() => {
                  if (confirming === 'delete') remove();
                  else if (confirming === 'void') action('void', 'Invoice voided');
                  else finalize(confirming === 'finalize_send');
                }}>
                {busy ? <Spinner size="sm" /> : 'Confirm'}
              </Button>
              <Button size="sm" variant="outline-secondary" onClick={() => setConfirming(null)} disabled={!!busy}>Cancel</Button>
            </div>
          </Alert>
        )}
      </Modal.Body>
      <Modal.Footer className="justify-content-between">
        <div>
          {invoice?.status === 'draft' && (
            <Button variant="outline-danger" onClick={() => setConfirming('delete')} disabled={!!busy}>
              <Trash className="me-1" /> Delete draft
            </Button>
          )}
          {invoice && invoice.status === 'open' && (
            <Button variant="outline-danger" onClick={() => setConfirming('void')} disabled={!!busy}>Void</Button>
          )}
        </div>
        <div className="d-flex gap-2">
          {isDraft ? (
            <>
              <Button variant="outline-secondary" onClick={saveDraft} disabled={!!busy || (!invoice && !caseId)}>
                {busy === 'save' ? <Spinner size="sm" /> : 'Save draft'}
              </Button>
              <Button variant="outline-primary" onClick={() => setConfirming('finalize')}
                disabled={!!busy || !caseId || stripeBlocked}
                title={stripeBlocked ? 'Connect Stripe for this organization first' : 'Create the invoice in Stripe and get a payment link'}>
                Finalize
              </Button>
              <Button variant="primary" onClick={() => setConfirming('finalize_send')}
                disabled={!!busy || !caseId || stripeBlocked || !customerEmail}
                title={!customerEmail ? 'Add a bill-to email first' : 'Finalize and have Stripe email the invoice'}>
                Finalize &amp; email
              </Button>
            </>
          ) : (
            invoice && (
              <>
                <Button variant="outline-secondary" onClick={() => action('refresh', 'Status refreshed from Stripe')} disabled={!!busy} title="Re-sync status from Stripe">
                  {busy === 'refresh' ? <Spinner size="sm" /> : <ArrowClockwise />}
                </Button>
                {invoice.invoice_pdf && (
                  <Button variant="outline-secondary" href={invoice.invoice_pdf} target="_blank" rel="noreferrer">PDF</Button>
                )}
                {invoice.status === 'open' && (
                  <>
                    <Button variant="outline-success" onClick={() => setShowMarkPaid(v => !v)} disabled={!!busy}>Paid outside Stripe…</Button>
                    <Button variant="primary" onClick={() => action('send', `Invoice emailed to ${invoice.customer_email}`)} disabled={!!busy || !invoice.customer_email}>
                      {busy === 'send' ? <Spinner size="sm" /> : invoice.sent_at ? 'Resend email' : 'Email invoice'}
                    </Button>
                  </>
                )}
              </>
            )
          )}
        </div>
      </Modal.Footer>
    </Modal>
  );
}
